/**
 * Connect flow, step 1 — design_handoff_integrations_page README §6.
 * Route, not a modal: signing in navigates away to the provider and back to
 * a callback URL, which a modal can't survive.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";
import ExplainerCard from "./IntegrationConnectPage/ExplainerCard";
import ScopesCard from "./IntegrationConnectPage/ScopesCard";
import { SCOPE_DEFINITIONS } from "./IntegrationConnectPage/scopeDefinitions";
import { useIntegrationStore } from "../../../hooks/redux/integration";
import { AvailableIntegrations } from "../consts/integrations";
import { openAuthWindow } from "../consts/pco";
import ApiError from "../component/ApiError";
import Icon from "../theme/untitled/icons/Icon";
import appConfig from "../../../appConfig";
import styles from "./IntegrationConnectPage.module.scss";
import { INTEGRATION_NAMES } from "../../../consts";

const IntegrationConnectPage = () => {
  const { provider } = useParams();
  const { integration, updateIntegration, setIntegrationModel } =
    useIntegrationStore();

  const definitions = SCOPE_DEFINITIONS[provider];
  const providerMeta = AvailableIntegrations.find((i) => i.type === provider);

  // New connection — no existing integration to read scope defaults from,
  // so start every scope on (mandatory ones stay locked on regardless),
  // matching the legacy form's `!integrationModel` default.
  const [scopes, setScopes] = useState(() =>
    Object.keys(definitions).reduce(
      (acc, name) => ({ ...acc, [name]: true }),
      {}
    )
  );

  useEffect(() => {
    setIntegrationModel(null);
  }, []);

  useEffect(() => {
    if (integration.updateSuccess && integration.model?.auth_url) {
      openAuthWindow(integration.model.auth_url);
    }
  }, [integration.updateSuccess]);

  const onScopeChange = useCallback((name, checked) => {
    setScopes((prev) => ({ ...prev, [name]: checked }));
  }, []);

  const onSignIn = useCallback(() => {
    const integration_scopes = Object.keys(definitions).map((name) => ({
      name,
      available: definitions[name].mandatory || scopes[name] ? 1 : 0,
    }));

    updateIntegration(undefined, {
      type: provider,
      name: `${INTEGRATION_NAMES[provider]} integration`,
      available: 1,
      is_auto_sync: 1,
      integration_scopes,
    });
  }, [definitions, scopes, provider, updateIntegration]);

  if (!definitions || !providerMeta) {
    return null;
  }

  return (
    <PageTemplate
      sidebarId="integrations"
      title={`Connect ${providerMeta.title}`}
      subtitle={`Step 1 of 2 — choose what ${appConfig.displayName} may read, then pick the calendars to sync.`}
    >
      <div className={styles.page}>
        <Link to="/integrations" className={styles.back}>
          <Icon name="ArrowNarrowLeft" size={16} />
          Integrations
        </Link>
        <ExplainerCard provider={provider} />
        <ScopesCard
          provider={provider}
          definitions={definitions}
          scopes={scopes}
          onScopeChange={onScopeChange}
          footerNote="Click the button below to sign in and grant the permissions you ticked. You can change them later."
          onSignIn={onSignIn}
          busy={integration.update}
        />
        <ApiError
          error={integration.updateError}
          defaultErrorMessage="Could not start the connection. Please try again."
        />
      </div>
    </PageTemplate>
  );
};

export default IntegrationConnectPage;
